import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { authApi, type RegisterAccountCheck } from '@/api/auth.api';
import type { ModuleEntry } from '@/config/moduleEntries';
import { enabledHeadModuleEntries } from '@/config/moduleEntries';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { STORAGE_KEYS } from '@/constants/storage';
import { resolveOwnerHomePath } from '@/utils/moduleAccess';
import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import {
  addOrgApplication,
  login,
  registerAccount,
  setWorkspaceModule,
} from '@/store/authSlice';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { SsoAuthButtons } from '@/core/auth/components/SsoAuthButtons';
import { Button } from '@/components/ui/Button';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import {
  clearPlatformOnboardingRequired,
  requireFattahOneOnboarding,
  resolveModuleIntent,
  writeOnboardingState,
  type OnboardingModule,
} from '@/modules/onboarding/onboardingState';
import type { ErpModule } from '@/api/platform.api';
import { resolveSsoErrorMessage, notifySsoError, ssoErrorToastGuardKey } from '@/utils/ssoErrorMessage';
import { validatePhoneValue } from '@/utils/phoneNumber';
import { detectBrowserTimezone } from '@/utils/phoneCountryDetection';
import { setSelectedCompany, setSelectedStore } from '@/store/appSlice';

const WORKSPACE_SCOPE_READY_KEY = 'fattahone_workspace_scope_ready';

function resetWorkspaceScopeForRegister(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_COMPANY);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_STORE);
    sessionStorage.removeItem(WORKSPACE_SCOPE_READY_KEY);
  } catch {
    /* ignore */
  }
}

const passwordSchema = (t: (key: string) => string) =>
  z
    .string()
    .min(8, t('register.passwordMin8'))
    .regex(/[A-Z]/, t('register.passwordUppercase'))
    .regex(/[a-z]/, t('register.passwordLowercase'))
    .regex(/[0-9]/, t('register.passwordNumber'))
    .regex(/[^A-Za-z0-9]/, t('register.passwordSpecial'));

const buildFullSchema = (t: (key: string) => string) =>
  z
    .object({
      name: z.string().min(2, t('register.nameRequired')),
      email: z.string().email(t('register.emailRequired')),
      phone: z
        .string()
        .min(1, t('register.phoneRequired'))
        .superRefine((val, ctx) => {
          const message = validatePhoneValue(val);
          if (message) ctx.addIssue({ code: 'custom', message });
        }),
      password: passwordSchema(t),
      confirmPassword: z.string(),
    })
    .refine((d) => d.password === d.confirmPassword, {
      message: t('register.passwordsMismatch'),
      path: ['confirmPassword'],
    });

type FormData = z.infer<ReturnType<typeof buildFullSchema>>;

type FlowStep = 'form' | 'existing';

function onboardingIntentFromErpModule(erpModule: ErpModule): OnboardingModule {
  const map: Partial<Record<ErpModule, OnboardingModule>> = {
    POS: 'POS',
    CRM: 'CRM',
    HR: 'HR',
    FINANCE: 'FINANCE',
    PROJECTS: 'PROJECTS',
    DESK: 'DESK',
    SOCIAL: 'SOCIAL',
  };
  return map[erpModule] ?? 'POS';
}

function moduleSlugForCheck(moduleEntry?: ModuleEntry, intentModule?: OnboardingModule | null): string | undefined {
  if (moduleEntry?.slug) return moduleEntry.slug;
  if (intentModule) return intentModule.toLowerCase();
  return undefined;
}

export interface RegisterWizardProps {
  moduleEntry?: ModuleEntry;
}

export function RegisterWizard({ moduleEntry }: RegisterWizardProps) {
  const { t } = useTranslation(['auth', 'navigation']);
  const fullSchema = useMemo(() => buildFullSchema(t), [t]);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoError = searchParams.get('ssoError');
  const ssoDetail = searchParams.get('ssoDetail');
  const intentModule = useMemo(() => {
    if (moduleEntry) {
      return onboardingIntentFromErpModule(moduleEntry.erpModule);
    }
    return resolveModuleIntent(location.search);
  }, [moduleEntry, location.search]);

  const signInPath = moduleEntry?.loginPath ?? ORG_SIGN_IN_PATH;
  const forgotPasswordPath = '/forgot-password';
  const entryModule = moduleEntry?.slug ?? moduleEntry?.erpModule;

  useEffect(() => {
    resetWorkspaceScopeForRegister();
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedStore(null));
  }, [dispatch]);

  const ssoToastHandledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ssoError) return;
    const fingerprint = `${ssoError}|${ssoDetail ?? ''}`;
    if (ssoToastHandledRef.current === fingerprint) return;
    ssoToastHandledRef.current = fingerprint;
    const message = resolveSsoErrorMessage(ssoError, ssoDetail, 'register');
    if (message) {
      notifySsoError(message, ssoErrorToastGuardKey('register', ssoError, ssoDetail));
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('ssoError');
        next.delete('ssoDetail');
        return next;
      },
      { replace: true },
    );
  }, [ssoError, ssoDetail, setSearchParams]);

  const [flowStep, setFlowStep] = useState<FlowStep>('form');
  const [existingAccount, setExistingAccount] = useState<RegisterAccountCheck | null>(null);
  const [formSnapshot, setFormSnapshot] = useState<FormData | null>(null);
  const [addingApp, setAddingApp] = useState(false);
  const [existingNotice, setExistingNotice] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(fullSchema), mode: 'onBlur' });

  const resetToForm = () => {
    setFlowStep('form');
    setExistingAccount(null);
    setFormSnapshot(null);
    setExistingNotice(null);
    clearErrors();
  };

  const applyOnboardingIntent = (options?: { newOrganization?: boolean; skipPlatformOnboarding?: boolean }) => {
    if (options?.skipPlatformOnboarding) {
      clearPlatformOnboardingRequired();
    } else if (options?.newOrganization) {
      requireFattahOneOnboarding();
    } else {
      clearPlatformOnboardingRequired();
    }
  };

  const createNewAccount = async (data: FormData) => {
    const [firstName, ...rest] = data.name.trim().split(/\s+/);
    const lastName = rest.join(' ') || undefined;
    await dispatch(
      registerAccount({
      organization: {
        name: `${data.name.trim()}'s Organization`,
        email: data.email,
        phone: data.phone,
        timezone: detectBrowserTimezone(),
      },
      ownerEmail: data.email,
      ownerPassword: data.password,
      ownerFirstName: firstName,
      ownerLastName: lastName,
      entryModule: moduleEntry?.slug ?? moduleEntry?.erpModule ?? intentModule?.toLowerCase(),
      deviceId: getOrCreateDeviceId(),
      }),
    ).unwrap();

    if (moduleEntry && intentModule) {
      applyOnboardingIntent({ newOrganization: true, skipPlatformOnboarding: true });
      dispatch(setWorkspaceModule(moduleEntry.erpModule));
      writeOnboardingState({
        step: 0,
        intentModule,
        selectedModules: [intentModule],
        selectedPlan: 'FREE',
      });
      notify.t.success('register.toastWelcomeModule', {
        ns: 'auth',
        module: localizeModuleLabel(moduleEntry.erpModule, t, moduleEntry.label),
      });
      navigate(moduleEntry.homePath, { replace: true });
      return;
    }

    applyOnboardingIntent({ newOrganization: true });
    writeOnboardingState({
      step: 0,
      intentModule,
      selectedModules: intentModule ? [intentModule] : ['POS'],
      selectedPlan: 'FREE',
    });
    notify.t.success('register.toastWelcome', { ns: 'auth' });
    navigate('/onboarding/products', { replace: true });
  };

  const signInExisting = async (data: FormData) => {
    const result = await dispatch(
      login({
        email: data.email.trim(),
        password: data.password,
        entryModule: moduleEntry?.slug,
      }),
    ).unwrap();
    applyOnboardingIntent();
    if (moduleEntry) {
      navigate(moduleEntry.homePath, { replace: true });
    } else {
      navigate(resolveOwnerHomePath(result.enabledModules ?? []), { replace: true });
    }
    notify.t.success('register.toastSignedIn', { ns: 'auth' });
  };

  const onSubmit = async (data: FormData) => {
    setExistingNotice(null);
    clearErrors();

    try {
      const check = await authApi.checkRegisterAccount({
        email: data.email.trim(),
        password: data.password,
        module: moduleSlugForCheck(moduleEntry, intentModule),
      });

      if (check.exists && !check.passwordValid) {
        setError('password', {
          type: 'manual',
          message: t('register.passwordMismatchEmail'),
        });
        setExistingNotice(t('register.wrongPasswordNotice'));
        return;
      }

      if (check.exists && check.passwordValid) {
        setFormSnapshot(data);
        setExistingAccount(check);
        setFlowStep('existing');
        return;
      }

      await createNewAccount(data);
    } catch (err) {
      showApiError(err, t('register.failedRegistration'));
    }
  };

  const addAppToOrg = async () => {
    const data = formSnapshot ?? watch();
    if (!existingAccount || !moduleEntry) return;
    setAddingApp(true);
    try {
      await dispatch(
        addOrgApplication({
          email: data.email.trim(),
          password: data.password,
          module: moduleEntry.slug,
          deviceId: getOrCreateDeviceId(),
        }),
      ).unwrap();
      applyOnboardingIntent();
      navigate(moduleEntry.homePath, { replace: true });
      notify.t.success('register.toastAppAdded', {
        ns: 'auth',
        app: localizeModuleLabel(moduleEntry.erpModule, t, moduleEntry.label),
        org: existingAccount.organizationName ?? t('register.yourOrganization'),
      });
    } catch (err) {
      showApiError(err, t('register.failedAddApp'));
    } finally {
      setAddingApp(false);
    }
  };

  const continueWithExisting = async () => {
    const data = formSnapshot;
    if (!data) return;
    setAddingApp(true);
    try {
      await signInExisting(data);
    } catch (err) {
      showApiError(err, t('register.failedLogin'));
    } finally {
      setAddingApp(false);
    }
  };

  if (flowStep === 'existing' && existingAccount?.passwordValid && formSnapshot) {
    const appLabel = moduleEntry
      ? localizeModuleLabel(moduleEntry.erpModule, t, moduleEntry.label)
      : undefined;
    const orgName = existingAccount.organizationName ?? t('register.yourOrganization');
    const enabledHeadApps = enabledHeadModuleEntries(existingAccount.enabledModules ?? []);

    return (
      <AuthCard>
        <p className="mb-4 text-center text-sm text-slate-600 dark:text-slate-400">
          {appLabel ? t('register.existingAddApp', { app: appLabel }) : t('register.existingLogin')}
        </p>
        <div className="space-y-4">
          <div className="flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
            <CheckCircleOutlineIcon className="mt-0.5 shrink-0" fontSize="small" />
            <div>
              <p className="font-medium">{t('register.accountVerified')}</p>
              <p className="mt-0.5 text-emerald-800/90 dark:text-emerald-200/90">
                {t('register.signedInAs', { email: formSnapshot.email })}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('register.organization')}</p>
            <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{orgName}</p>
            {enabledHeadApps.length > 0 && (
              <>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-500">{t('register.yourApplications')}</p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {enabledHeadApps.map((entry) => (
                    <li
                      key={entry.erpModule}
                      className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-600"
                    >
                      {localizeModuleLabel(entry.erpModule, t, entry.label)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {appLabel && existingAccount.moduleAlreadyEnabled ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              <p>{t('register.appAlreadyEnabled', { app: appLabel })}</p>
            </div>
          ) : appLabel ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('register.addAppPrompt', { app: appLabel, org: orgName })}
            </p>
          ) : (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {t('register.existingOrgPrompt', { org: orgName })}
            </p>
          )}

          <div className="flex flex-col gap-2 pt-1">
            {appLabel && !existingAccount.moduleAlreadyEnabled && (
              <AuthButton type="button" loading={addingApp} onClick={() => void addAppToOrg()}>
                {t('register.addAppButton', { app: appLabel, org: orgName })}
              </AuthButton>
            )}
            {(existingAccount.moduleAlreadyEnabled || !appLabel) && (
              <AuthButton type="button" loading={addingApp} onClick={() => void continueWithExisting()}>
                {t('register.loginToOrg', { org: orgName })}
              </AuthButton>
            )}
            {appLabel && !existingAccount.moduleAlreadyEnabled && (
              <Button type="button" variant="secondary" className="w-full" onClick={() => void continueWithExisting()}>
                {t('register.loginWithoutAdding', { app: appLabel })}
              </Button>
            )}
            <Link to={signInPath} className="text-center text-sm text-primary-600 hover:underline dark:text-primary-400">
              {t('register.goToLogin')}
            </Link>
            <button type="button" className="text-xs text-slate-500 hover:underline" onClick={resetToForm}>
              {t('register.useDifferentEmail')}
            </button>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <p className="mb-4 text-center text-sm text-slate-600 dark:text-slate-400">{t('register.intro')}</p>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {existingNotice && (
            <div
              className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
              role="alert"
            >
              <ErrorOutlineIcon className="mt-0.5 shrink-0" fontSize="small" />
              <div>
                <p className="font-medium">{t('register.alreadyRegisteredTitle')}</p>
                <p className="mt-0.5">{existingNotice}</p>
                <p className="mt-2">
                  <Link to={signInPath} className="font-medium underline">
                    {t('register.login')}
                  </Link>
                  {' · '}
                  <Link to={forgotPasswordPath} className="font-medium underline">
                    {t('register.resetPassword')}
                  </Link>
                </p>
              </div>
            </div>
          )}

          <AuthInput label={t('register.name')} autoComplete="name" {...register('name')} error={errors.name?.message} />
          <AuthInput
            label={t('register.email')}
            type="email"
            autoComplete="email"
            {...register('email')}
            error={errors.email?.message}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <AuthInput
                label={t('register.phone')}
                type="tel"
                autoComplete="tel"
                name={field.name}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                error={fieldState.error?.message}
              />
            )}
          />
          <AuthInput
            label={t('register.password')}
            type="password"
            autoComplete="new-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <AuthInput
            label={t('register.confirmPassword')}
            type="password"
            autoComplete="new-password"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />

          <AuthButton type="submit" loading={isSubmitting}>
            {t('register.continue')}
          </AuthButton>
        </form>

        <SsoAuthButtons
          registerIntent
          entryModule={entryModule}
          redirectAfter={moduleEntry?.homePath ?? '/onboarding/products'}
          googleLabel={t('register.googleSignUp')}
        />

        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('register.alreadyRegistered')}{' '}
          <Link to={signInPath} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            {t('register.login')}
          </Link>
        </p>
      </AuthCard>
  );
}
